import { makeStyles } from 'tss-react/mui'
import React, { FC } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { ElementContent } from 'react-markdown/lib/ast-to-react'
import { colors } from '@hazadapt-git/public-core-base'
import { useContentHighlighting } from '../../hooks'
import { StyleOverrides, TTS_CONFIG } from '../../lib/entities'

interface MarkdownProps {
    containerClassName?: string
    className?: string
    onLinkPress?(url: string): any | Promise<any>
    content: string
    highlightedSentenceIndex?: number
    styleOverrides?: StyleOverrides
    transformLinkUri?(
        href: string,
        children: ElementContent[],
        title: string | null
    ): string
}

export const Markdown: FC<MarkdownProps> = React.memo(
    (props: MarkdownProps) => {
        const { classes: mdClasses } = useMdStyles()

        // Highlighting and smart scroll for TTS support
        const { getHighlightClass } = useContentHighlighting({
            content: props.content,
            highlightedSentenceIndex: props.highlightedSentenceIndex,
            highlightClassName: mdClasses.highlighted,
            highlightSelector: `.${mdClasses.highlighted}`,
            scrollThreshold: TTS_CONFIG.SCROLL.viewportThreshold,
            scrollBehavior: TTS_CONFIG.SCROLL.behavior as 'auto' | 'smooth',
            scrollBlock: TTS_CONFIG.SCROLL.block as 'start' | 'center' | 'end' | 'nearest',
        })

        return (
            <div className={props.containerClassName}>
                <ReactMarkdown
                    className={props.className}
                    transformLinkUri={props.transformLinkUri}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                        h1: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <h1
                                    {...elemProps}
                                    className={mdClasses.h1}
                                    style={props.styleOverrides?.h1}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </h1>
                            )
                        },
                        h2: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <h2
                                    {...elemProps}
                                    className={mdClasses.h2}
                                    style={props.styleOverrides?.h2}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </h2>
                            )
                        },
                        h3: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <h3
                                    {...elemProps}
                                    className={mdClasses.h3}
                                    style={props.styleOverrides?.h3}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </h3>
                            )
                        },
                        h4: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <h4
                                    {...elemProps}
                                    className={mdClasses.h4}
                                    style={props.styleOverrides?.h4}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </h4>
                            )
                        },
                        h5: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <h5
                                    {...elemProps}
                                    className={mdClasses.h5}
                                    style={props.styleOverrides?.h5}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </h5>
                            )
                        },
                        p: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <p
                                    className={mdClasses.p}
                                    style={props.styleOverrides?.p}
                                    {...elemProps}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </p>
                            )
                        },
                        li: ({ node, children, ...elemProps }) => {
                            const { ordered, ...liProps } = elemProps
                            const highlightClass = getHighlightClass(children)
                            return (
                                <li
                                    className={mdClasses.li}
                                    style={props.styleOverrides?.li}
                                    {...liProps}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </li>
                            )
                        },
                        ul: ({ node, ...elemProps }) => {
                            const { ordered, ...ulProps } = elemProps
                            return (
                                <ul
                                    style={props.styleOverrides?.ul}
                                    {...ulProps}
                                />
                            )
                        },
                        ol: ({ node, ...elemProps }) => (
                            <ol
                                style={props.styleOverrides?.ol}
                                {...elemProps}
                            />
                        ),
                        a: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <a
                                    {...elemProps}
                                    onClick={(e) => {
                                        if (
                                            props.onLinkPress &&
                                            elemProps.href
                                        ) {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            props.onLinkPress(elemProps.href)
                                            return false
                                        }
                                    }}
                                    className={mdClasses.a}
                                    style={props.styleOverrides?.a}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </a>
                            )
                        },
                        blockquote: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <blockquote
                                    className={mdClasses.blockquote}
                                    style={props.styleOverrides?.blockquote}
                                    {...elemProps}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </blockquote>
                            )
                        },
                        mark: ({ node, ...elemProps }) => (
                            <mark
                                {...elemProps}
                                style={{
                                    background: '#ffe066',
                                    borderRadius: '4px',
                                    padding: '0 2px',
                                    transition: 'background 0.2s',
                                }}
                            />
                        ),
                        strong: ({ node, children, ...elemProps }) => {
                            const highlightClass = getHighlightClass(children)
                            return (
                                <strong
                                    className={mdClasses.strong}
                                    style={props.styleOverrides?.strong}
                                    {...elemProps}
                                >
                                    {highlightClass ? (
                                        <span className={highlightClass}>
                                            {children}
                                        </span>
                                    ) : (
                                        children
                                    )}
                                </strong>
                            )
                        },
                    }}
                    children={props.content}
                />
            </div>
        )
    }
)

export const useMdStyles = makeStyles()({
    p: {
        fontSize: '1rem',
        paddingRight: '0.625rem',
    },
    li: {
        paddingTop: '0.15625rem',
        paddingBottom: '0.15625rem',
    },
    a: {
        color: 'blue',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    blockquote: {
        borderLeft: `0.25rem ${colors.grays.SILVER} solid`,
        paddingLeft: '0.25rem',
        marginInlineStart: '0.25rem',
        marginBlockEnd: '1.5rem',
    },
    strong: {
        fontWeight: 600,
    },
    h1: {
        fontSize: '2rem',
        fontWeight: 600,
        color: colors.grays.NOIR,
        lineHeight: '2.5rem',
    },
    h2: {
        fontSize: '1.75rem',
        fontWeight: 600,
        color: colors.grays.NOIR,
        lineHeight: '2.25rem',
    },
    h3: {
        fontSize: '1.5rem',
        fontWeight: 500,
        color: colors.grays.NOIR,
        lineHeight: '1.875rem',
    },
    h4: {
        fontSize: '1.25rem',
        fontWeight: 500,
        color: colors.grays.NOIR,
        lineHeight: '1.5rem',
    },
    h5: {
        fontSize: '1rem',
        fontStyle: 'italic',
        color: colors.grays.NOIR,
        lineHeight: '1.25rem',
    },
    highlighted: TTS_CONFIG.HIGHLIGHT,
})
